import { useEffect } from 'react'
import { Alert, Box, Skeleton } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { useDashboardStore } from '@/stores/dashboardStore'
import { useDashboardRealtime } from '@/realtime/useDashboardRealtime'
import { displayErrorMessage } from '@/utils/error'
import { DashboardGrid } from './DashboardGrid'
import { WidgetCard } from './WidgetCard'
import { WIDGET_COMPONENTS } from './widgets'
import { EmptyHint } from './widgets/EmptyHint'
import type { DashboardRole, LayoutItem } from '@/types/dashboard'

interface RoleDashboardProps {
  role: DashboardRole
}

/**
 * Сетка виджетов одной роли: bootstrap стора, realtime-подписка, рендер грида.
 */
export function RoleDashboard({ role }: RoleDashboardProps) {
  const { t } = useTranslation()
  const userId = useAuthStore((s) => s.user?.id ?? null)

  const catalog = useDashboardStore((s) => s.catalog)
  const layout = useDashboardStore((s) => s.layout)
  const widgets = useDashboardStore((s) => s.widgets)
  const editMode = useDashboardStore((s) => s.editMode)
  const draftItems = useDashboardStore((s) => s.draftItems)
  const isBootstrapping = useDashboardStore((s) => s.isBootstrapping)
  const error = useDashboardStore((s) => s.error)
  const bootstrap = useDashboardStore((s) => s.bootstrap)
  const reset = useDashboardStore((s) => s.reset)
  const setDraftItems = useDashboardStore((s) => s.setDraftItems)
  const toggleHidden = useDashboardStore((s) => s.toggleHidden)
  const clearError = useDashboardStore((s) => s.clearError)

  useEffect(() => {
    void bootstrap(role)
    return () => {
      reset()
    }
  }, [bootstrap, reset, role])

  useDashboardRealtime(userId)

  if (isBootstrapping || !catalog || !layout) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {[0, 1, 2, 3].map((index) => (
          <Skeleton key={index} variant="rectangular" height={180} sx={{ borderRadius: 1 }} />
        ))}
      </Box>
    )
  }

  const items = editMode && draftItems ? draftItems : layout.items

  const renderWidget = (item: LayoutItem) => {
    const key = item.widget_key
    const state = widgets[key]
    const WidgetComponent = WIDGET_COMPONENTS[key]
    const payload = state?.data ?? null

    return (
      <WidgetCard
        title={t(`dashboard.widgets.${key}.title`)}
        editMode={editMode}
        loading={Boolean(state?.loading) && !payload}
        error={
          state?.error
            ? displayErrorMessage(state.error, t('common.error'))
            : null
        }
        onHide={editMode ? () => toggleHidden(key) : undefined}
      >
        {payload === null || payload.error === true ? (
          <EmptyHint
            fallbackKey={payload?.error === true ? 'dashboard.widget.error' : 'dashboard.widget.empty'}
          />
        ) : WidgetComponent ? (
          <WidgetComponent payload={payload} role={role} rows={item.h} />
        ) : (
          <EmptyHint hint={typeof payload.hint === 'string' ? payload.hint : undefined} />
        )}
      </WidgetCard>
    )
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {displayErrorMessage(error, t('common.error'))}
        </Alert>
      )}
      <DashboardGrid
        items={items}
        cols={catalog.grid.cols}
        rowHeight={catalog.grid.row_height}
        maxH={catalog.grid.max_h}
        editMode={editMode}
        onItemsChange={setDraftItems}
        renderWidget={renderWidget}
      />
    </Box>
  )
}
